import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { JournalsApi } from '../../../apis/JournalsApi/JournalsApi';
import { IJournalEntryInput } from '../Journal';

// Query Keys
export const patientJournalEntriesKeys = {
    all: ['patientJournalEntries'] as const,
    byPatient: (patientId: number) => [...patientJournalEntriesKeys.all, 'patient', patientId] as const,
    byDateRange: (patientId: number, startDate: string, endDate: string) =>
        [...patientJournalEntriesKeys.byPatient(patientId), 'dateRange', startDate, endDate] as const,
    detail: (id: number) => [...patientJournalEntriesKeys.all, 'detail', id] as const,
};

// Queries
export const usePatientJournalEntries = (patientId: number) => {
    return useQuery({
        queryKey: patientJournalEntriesKeys.byPatient(patientId),
        queryFn: () => JournalsApi.getEntriesByPatientId(patientId),
        enabled: !!patientId,
    });
};

export const usePatientJournalEntry = (id: number) => {
    return useQuery({
        queryKey: patientJournalEntriesKeys.detail(id),
        queryFn: () => JournalsApi.getEntryById(id),
        enabled: !!id,
    });
};

// Mutations
export const useCreatePatientJournalEntry = () => {
    const queryClient = useQueryClient();
    
    return useMutation({
        mutationFn: (entry: IJournalEntryInput) => JournalsApi.createEntry(entry),
        onSuccess: (_, variables) => {
            // Invalidate patient journal entries
            queryClient.invalidateQueries({
                queryKey: patientJournalEntriesKeys.byPatient(variables.patientID)
            });
        },
    });
};

export const useUpdatePatientJournalEntry = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, entry }: { id: number; entry: IJournalEntryInput }) =>
            JournalsApi.updateEntry(id, entry),
        onSuccess: (_, variables) => {
            // Invalidate related queries
            queryClient.invalidateQueries({
                queryKey: patientJournalEntriesKeys.byPatient(variables.entry.patientID)
            });
            queryClient.invalidateQueries({
                queryKey: patientJournalEntriesKeys.detail(variables.id)
            });
        },
    });
};

export const useDeletePatientJournalEntry = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: number) => JournalsApi.deleteEntryById(id),
        onSuccess: () => {
            // Invalidate all patient journal entries
            queryClient.invalidateQueries({
                queryKey: patientJournalEntriesKeys.all
            });
        },
    });
};